import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  SearchBar,
  Checkbox,
  Button,
} from '@skyline-platform/alchemy';
import {
  MenuItem,
  Menu,
} from '@mui/material';
import './SearchBarComponent.css';
import PropTypes from 'prop-types';

export const SearchBarComponent = ({
  searchOptions,
  fieldName,
  selectedFields,
  setSelectedFields,
  userIsAdmin,
  onSearch,
}) => {
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [allSelected, setAllSelected] = useState(true);

  // Tracks the current search term so effects and callbacks can read the latest value
  // without needing it in their dependency arrays (which would cause re-renders).
  const searchWordRef = useRef('');

  // Tracks the last value that was actually dispatched to onSearch. Prevents firing the
  // API search again when the Alchemy SearchBar re-emits the same value on focus.
  const lastSearchedRef = useRef('');

  // Tracks the previous selectedFields array so we can detect a real change vs a
  // re-render where the array reference changed but the content didn't.
  const selectedFieldsRef = useRef(selectedFields);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setSearchDropdownOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchDropdownOpen(false);
  };

  // Called by the Alchemy SearchBar after the user stops typing (debounce is handled
  // by the SearchBar component via the searchDelay prop, not here).
  // Guards against re-firing onSearch when the value hasn't actually changed — the
  // Alchemy SearchBar re-emits the current value when the input is focused, which
  // would otherwise trigger a spurious API call.
  const handleSearch = useCallback((targetVal) => {
    searchWordRef.current = targetVal;
    if (onSearch && targetVal !== lastSearchedRef.current) {
      lastSearchedRef.current = targetVal;
      onSearch(targetVal, selectedFields);
    }
  }, [onSearch, selectedFields]);

  // Handles checking/unchecking individual fields or the "Select All" option in the
  // search field dropdown. Encodes both the field value and its current checked state
  // in the MenuItem's data-value attribute to avoid managing per-item state.
  const handleFieldChange = (e) => {
    e.preventDefault();

    const data = e.currentTarget.dataset.value;
    const [value, checked] = data.split(',');

    if (value === 'selectAll') {
      if (allSelected) {
        setSelectedFields([]);
      } else {
        setSelectedFields(searchOptions.map((option) => option.value));
      }

      setAllSelected((prev) => !prev);

    } else {
      setSelectedFields((prev) => {
        const updatedFields = checked === 'true'
          ? prev.filter((field) => field !== value)
          : prev.concat(value);

        setAllSelected(updatedFields.length === searchOptions.length);

        return updatedFields;
      });
    }
  };

  // Re-fires onSearch when the user toggles which fields to search across, but only
  // if there is already a search term active. Uses selectedFieldsRef to distinguish
  // a real field selection change from a re-render where the array reference changed
  // but selectedFields was not actually modified.
  useEffect(() => {
    if (selectedFields === selectedFieldsRef.current) return;
    selectedFieldsRef.current = selectedFields;
    if (searchWordRef.current?.length > 0) {
      onSearch?.(searchWordRef.current, selectedFields);
    }
  }, [selectedFields, onSearch]);

  // Ref to the underlying Alchemy SearchBar DOM element, reserved for any imperative
  // access that may be needed (e.g. programmatically clearing the input).
  const searchbarRef = useRef(null);
  return (
    <gar-searchbar>
      <Button
        label='Search'
        type='text'
        onClick={searchDropdownOpen ? handleClose : handleOpen}
        iconRight={searchDropdownOpen ? 'fa fa-angle-up' : 'fa fa-angle-down'}
        aria-label="Search filter options"
        aria-haspopup="menu"
        aria-expanded={searchDropdownOpen}
      />
      <Menu
        anchorEl={anchorEl}
        open={searchDropdownOpen}
        onClose={handleClose}
        autoFocus={false}
      >
        <MenuItem
          onClick={(e) => handleFieldChange(e)}
          data-value={`selectAll,${allSelected}`}
          className='table-search-menu-item'
        >
          <Checkbox
            label='Select All'
            labelPlacement='end'
            checked={allSelected}
            value='selectAll'
          />
        </MenuItem>
        {searchOptions.map((option) => {
          return (
            <MenuItem
              className='table-search-menu-item'
              key={option.key}
              onClick={(e) => handleFieldChange(e)}
              data-value={`${option.value},${selectedFields.includes(option.value)}`}
            >
              <Checkbox
                label={option.key}
                labelPlacement='end'
                checked={selectedFields.includes(option.value)}
                aria-label={option.value}
                key={option.value}
                value={option.value}
              />
            </MenuItem>
          );
        })}
      </Menu>
      <SearchBar
        handleSearch={handleSearch}
        searchDelay={500}
        inputBackgroundColor='var(--vp-white)'
        ref={searchbarRef}
        accessibleLabel={fieldName}
        className={!userIsAdmin ? 'search-input margin-right' : 'search-input'}
      />
    </gar-searchbar>
  );
};

SearchBarComponent.propTypes = {
  fieldName: PropTypes.string,
  onSearch: PropTypes.func,
  searchOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  selectedFields: PropTypes.array,
  setSelectedFields: PropTypes.func,
  userIsAdmin: PropTypes.bool,
};
